import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import { Button } from 'reactstrap';
import './ActionDefinitionForm.scss';
import { RadioGroup } from '@mui/material';
import { Form } from '@availity/form';
import ReviewDocumentForm from './ReviewDocumentForm/ReviewDocumentForm';
import ExpandableRadioButton from '../../../components/ExpandableRadioButton/ExpandableRadioButton';
import './TimelineActionYupValidator.ts';
import { useDispatch } from 'react-redux';
import { GetDocumentTemplates } from '../../../slices/marketplace/timeline/thunks/get-document-templates.thunk';
import { TimelineParty } from '../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { TimelineEvent } from '../../../slices/marketplace/timeline/models/timeline-event.model';
import {
    createDefaultDocumentDetails,
    DocumentDetails,
} from '../../../slices/marketplace/timeline/models/document-details.model';
import {
    BuyerActionTypeGroups,
    BuyerLinkActionGroupOptions,
    MarketplaceEventActionTypeList,
    SellerActionTypeGroups,
    SellerAndBuyerActionTypeGroups,
    SellerAndBuyerLinkActionGroupOptions,
    SellerLinkActionGroupOptions,
    TimelineActionType,
    VetValueActionTypeGroups,
    VetValueLinkActionGroupOptions,
} from '../../../slices/marketplace/timeline/models/timeline-action-type.model';
import {
    defaultTimelineAction,
    TimelineAction,
} from '../../../slices/marketplace/timeline/models/timeline-actions.model';
import { DocumentSection } from '../../../slices/marketplace/timeline/models/document-section.model';
import { SellerEngagementDocumentForm } from './SellerEngagementDocumentForm/SellerEngagementDocumentForm';
import { useStateSelector } from '../../../store/selectors';
import { EngagementDocumentSource } from '../../../slices/marketplace/timeline/models/enums/engagement-document-source';
import createNotification from '../../../utils/createNotification';
import DocumentsHistoryPopup from '../DocumetsHistoryPopup/DocumetsHistoryPopup';
import TeaserInfoModal from '../TeaserInfoModal/TeaserInfoModal';
import { TeaserPracticeData } from '../../../slices/marketplace/timeline/models/teaser-data.model';
import { NdaTermSelectionDocumentTemplatesState } from '../../../slices/marketplace/timeline/models/nda-term-selection';
import BuyerSelectionForm from './BuyerSelectionForm/BuyerSelectionForm';
import { SellerDataAndDocumentRequestForm } from './SellerDataAndDocumentRequestForm/SellerDataAndDocumentRequestForm';
import { LinkedDataRequestProject } from '../../../slices/marketplace/timeline/models/linked-data-request-project.model';
import { TimelineEventStatus } from '../../../slices/marketplace/timeline/models/enums/timeline-event-status.enum';
import { EngagementProcessStatus } from '../../../slices/marketplace/timeline/models/enums/engagement-process-status.enum';
import { SellerNdaTermSelectionForm } from './SellerNdaTermSelectionForm/SellerNdaTermSelectionForm';
import { BuyerDocumentExecutionForm } from './BuyerDocumentExecutionForm/BuyerDocumentExecutionForm';
import { BuyerExpressionOfInterestForm } from './BuyerExpresstionOfInterestForm/BuyerExpresstionOfInterestForm';
import { BuyerUploadDocumentForm } from './BuyerUploadDocumentForm/BuyerUploadDocumentForm';
import {
    DocumentDetailsContentSubtype,
    DocumentDetailsContentSubtypeEnumToStringMapping as subTypeEnumToStringMap,
} from '../../../slices/marketplace/timeline/models/enums/document-details-content-subtype';
import { BuyerExpressionOfInterestSettings } from '../../../slices/marketplace/timeline/models/buyer-expression-of-interest-settings.model';
import { LevelOfService } from '../../../slices/marketplace/timeline/models/enums/level-of-service.enum';

interface ActionDefinitionFormProps {
    party: TimelineParty;
    onSubmit(
        action: TimelineAction,
        masterDocumentDetails?: DocumentDetails,
        additionalDocumentDetails?: DocumentDetails[],
        dataRequestProject?: LinkedDataRequestProject,
        ndaTermSelection?: NdaTermSelectionDocumentTemplatesState,
        expressionOfInterestSettings?: BuyerExpressionOfInterestSettings
    ): void;
    disabled: boolean;
    currentEvent: TimelineEvent;
    linkedAction?: TimelineAction;
    timelineEvents: TimelineEvent[];
    getDocumentDetails(): DocumentDetails | null;
    getDataRequestProject(id?: string): LinkedDataRequestProject | null;
    downloadFile(documentId?: string, documentDetails?: DocumentDetails): void;
    documentsSections: DocumentSection[];
    documentDetailsList: DocumentDetails[];
    dataRequestProjects: LinkedDataRequestProject[];
    timelineId: number;
    teaserData: TeaserPracticeData[];
    onTeaserSave: (newValues: TeaserPracticeData[]) => Promise<void>;
    onUpdateConfirm: () => void;
    isTeaserConfirmed: boolean;
    setIsFileUploading(isUploading: boolean): void;
    setDocumentSectionList: (documentSections: DocumentSection[]) => void;
    isTemplate: boolean;
    ndaTermSelectionState: NdaTermSelectionDocumentTemplatesState;
    expressionOfInterestSettings: BuyerExpressionOfInterestSettings;
    levelOfService: LevelOfService;
}

const ActionDefinitionForm = forwardRef(
    (props: ActionDefinitionFormProps, ref) => {
        const timeline = useStateSelector(
            (state) => state.timelines.currentTimeline
        );

        const dispatch = useDispatch() as any;

        const [actionTypeValue, setActionTypeValue] = useState<
            | BuyerActionTypeGroups
            | SellerActionTypeGroups
            | VetValueActionTypeGroups
            | SellerAndBuyerActionTypeGroups
        >(null);
        const [
            isDocumentsHistoryPopupVisible,
            setIsDocumentsHistoryPopupVisible,
        ] = useState(false);
        const [documenstHistorySubType, setDocumenstHistorySubType] =
            useState<DocumentDetailsContentSubtype | null>(null);
        const [selectedDocumentIdForHistory, setSelectedDocumentIdForHistory] =
            useState<string | null>(null);

        const [isTeaserInfoModalVisible, setIsTeaserInfoModalVisible] =
            useState(false);

        const [masterDocumentDetails, setMasterDocumentDetails] = useState({
            ...(props.getDocumentDetails() ??
                createDefaultDocumentDetails(true)),
        });

        useEffect(() => {
            const selectedGroup =
                MarketplaceEventActionTypeList.find(
                    (f) => f.id === props.linkedAction?.actionType
                )?.group ?? null;

            setActionTypeValue(selectedGroup);
            const MAX_INT_32 = 2147483647;
            dispatch(
                GetDocumentTemplates({
                    filter: {
                        filters: [],
                        logic: '',
                    },
                    pageNumber: 1,
                    pageSize: MAX_INT_32,
                    sort: {
                        dir: 'asc',
                        field: 'name',
                    },
                })
            );
        }, []);

        const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            const indexOfEventWithRefToThisAction = props.linkedAction
                ? props.timelineEvents.findIndex(
                      (e) =>
                          e.linkedAction?.actionReferenceId ===
                          props.linkedAction?.id
                  )
                : -1;
            if (indexOfEventWithRefToThisAction > -1) {
                event.preventDefault();
                event.stopPropagation();
                createNotification(
                    `You can't change the Engagement document type because it is used in the event with index ${
                        indexOfEventWithRefToThisAction + 1
                    }.`,
                    'error'
                );

                return;
            }

            setActionTypeValue(Number(event.target.value));
            setMasterDocumentDetails(undefined);
        };

        const formRef = useRef<any>();

        useImperativeHandle(ref, () => ({
            handleSubmit() {
                formRef?.current?.handleSubmit();
            },
        }));

        const getBuyerActionGroupForm = (
            actionGroup: BuyerActionTypeGroups
        ) => {
            switch (actionGroup) {
                case BuyerActionTypeGroups.DocumentExecution:
                    return (
                        <BuyerDocumentExecutionForm
                            ref={formRef}
                            disabled={props.disabled}
                            party={props.party}
                            onSubmit={props.onSubmit}
                            documentDetails={masterDocumentDetails}
                            documentDetailsList={props.documentDetailsList}
                            setDocumentSectionList={
                                props.setDocumentSectionList
                            }
                            documentsSections={props.documentsSections}
                        />
                    );
                case BuyerActionTypeGroups.ExpresstionOfInterest:
                    return (
                        <BuyerExpressionOfInterestForm
                            ref={formRef}
                            disabled={props.disabled}
                            onSubmit={(
                                action,
                                expressionOfInterestSettings
                            ) => {
                                props.onSubmit(
                                    action,
                                    undefined,
                                    undefined,
                                    undefined,
                                    undefined,
                                    expressionOfInterestSettings
                                );
                            }}
                            linkedAction={props.linkedAction}
                            timelineEvents={props.timelineEvents}
                            currentEvent={props.currentEvent}
                            levelOfService={props.levelOfService}
                            expressionOfInterestSettings={
                                props.expressionOfInterestSettings
                            }
                        />
                    );
                case BuyerActionTypeGroups.ReviewDocument:
                    return (
                        <ReviewDocumentForm
                            ref={formRef}
                            onSubmit={props.onSubmit}
                            documentDetails={masterDocumentDetails}
                            disabled={props.disabled}
                            downloadFile={props.downloadFile}
                            currentEvent={props.currentEvent}
                            timelineEvents={props.timelineEvents}
                            documentDetailsList={props.documentDetailsList}
                            documentsSections={props.documentsSections}
                            setDocumentSectionList={
                                props.setDocumentSectionList
                            }
                        />
                    );
                case BuyerActionTypeGroups.UploadDocument:
                    return (
                        <BuyerUploadDocumentForm
                            ref={formRef}
                            disabled={props.disabled}
                            party={props.party}
                            onSubmit={props.onSubmit}
                            documentDetails={masterDocumentDetails}
                            documentDetailsList={props.documentDetailsList}
                            setDocumentSectionList={
                                props.setDocumentSectionList
                            }
                            documentsSections={props.documentsSections}
                        />
                    );
                case BuyerActionTypeGroups.Custom:
                    return (
                        <RadioGroup style={{ marginLeft: '1rem' }}>
                            {[
                                TimelineActionType.BuyerScheduleMeeting,
                                TimelineActionType.BuyerLoiInformation,
                            ].map((actionType) => {
                                return (
                                    <ExpandableRadioButton
                                        disabled={props.disabled}
                                        value={actionType}
                                        label={
                                            MarketplaceEventActionTypeList.find(
                                                (f) => f.id === actionType
                                            )?.name
                                        }
                                        isExpanded={false}
                                        expandableContent={''}
                                    />
                                );
                            })}
                        </RadioGroup>
                    );
                default:
                    break;
            }
        };

        const getSellerActionGroupForm = (
            actionGroup: SellerActionTypeGroups
        ) => {
            switch (actionGroup) {
                case SellerActionTypeGroups.DataAndDocumentRequest:
                    return (
                        <SellerDataAndDocumentRequestForm
                            ref={formRef}
                            disabled={props.disabled}
                            currentEvent={props.currentEvent}
                            onSubmit={props.onSubmit}
                            dataRequestProjects={props.dataRequestProjects}
                        />
                    );
                case SellerActionTypeGroups.EngagementDocument:
                    return (
                        <SellerEngagementDocumentForm
                            ref={formRef}
                            onSubmit={props.onSubmit}
                            disabled={props.disabled}
                            downloadFile={props.downloadFile}
                            masterDocumentDetails={masterDocumentDetails}
                            documentsSections={props.documentsSections}
                            timelineEvents={props.timelineEvents}
                            linkedAction={props.linkedAction}
                            currentEvent={props.currentEvent}
                            setIsFileUploading={props.setIsFileUploading}
                            setIsDocumentsHistoryPopupVisible={(
                                isVisible: boolean,
                                documentId?: string,
                                subtype?: DocumentDetailsContentSubtype
                            ) => {
                                setIsDocumentsHistoryPopupVisible(isVisible);
                                setSelectedDocumentIdForHistory(documentId);
                                setDocumenstHistorySubType(subtype ?? null);
                            }}
                            setDocumentSectionList={
                                props.setDocumentSectionList
                            }
                            documentDetailsList={props.documentDetailsList}
                            dataRequestProjects={props.dataRequestProjects}
                            isTemplate={props.isTemplate}
                        />
                    );
                case SellerActionTypeGroups.BuyerSelection:
                    return (
                        <BuyerSelectionForm
                            ref={formRef}
                            onSubmit={props.onSubmit}
                            disabled={props.disabled}
                            linkedAction={props.linkedAction}
                            currentEvent={props.currentEvent}
                            timelineEvents={props.timelineEvents}
                            party={props.party}
                            documentsSections={props.documentsSections}
                            getDocumentDetails={props.getDocumentDetails}
                            setDocumentSectionList={
                                props.setDocumentSectionList
                            }
                            documentDetailsList={props.documentDetailsList}
                            excludedActionTypes={[
                                TimelineActionType.SellerBuyerSelectionIdNda,
                                TimelineActionType.SellerBuyerSelectionDetailedFinancialModel,
                            ]}
                        />
                    );
                case SellerActionTypeGroups.TeaserApproval:
                    return (
                        <Form
                            innerRef={formRef}
                            initialValues={{
                                actionType:
                                    TimelineActionType.SellerTeaserApproval,
                            }}
                            onSubmit={async (values) => {
                                props.onSubmit({
                                    ...defaultTimelineAction,
                                    actionType: Number(values.actionType),
                                });
                            }}>
                            <Button
                                className="teaser-popup-btn"
                                onClick={() =>
                                    setIsTeaserInfoModalVisible(true)
                                }
                                disabled={props.isTemplate}
                                type="button"
                                color="primary">
                                {`${
                                    props.isTeaserConfirmed
                                        ? 'View'
                                        : 'Configure'
                                } Teaser Information`}
                            </Button>
                        </Form>
                    );
                case SellerActionTypeGroups.Custom:
                    return (
                        <RadioGroup style={{ marginLeft: '1rem' }}>
                            {[
                                TimelineActionType.SellerCustomProcessDuration,
                                TimelineActionType.SellerCustomMeetingAvailability,
                            ].map((actionType) => {
                                return (
                                    <ExpandableRadioButton
                                        disabled={props.disabled}
                                        value={actionType}
                                        label={
                                            MarketplaceEventActionTypeList.find(
                                                (f) => f.id === actionType
                                            )?.name
                                        }
                                        isExpanded={false}
                                        expandableContent={''}
                                    />
                                );
                            })}
                        </RadioGroup>
                    );
                default:
                    break;
            }
        };

        const buyerActionsForm = () => (
            <RadioGroup value={actionTypeValue} onChange={handleChange}>
                {BuyerLinkActionGroupOptions.map((actionGroup) => {
                    //remove isImplemented flag when all actions are available
                    switch (actionGroup.id) {
                        case BuyerActionTypeGroups.ExpresstionOfInterest:
                            return actionGroup.isImplemented ? (
                                <ExpandableRadioButton
                                    disabled={props.disabled}
                                    value={actionGroup.id}
                                    label={actionGroup.name}
                                    isExpanded={
                                        actionTypeValue === actionGroup.id
                                    }
                                    expandableContent={getBuyerActionGroupForm(
                                        actionGroup.id
                                    )}
                                />
                            ) : (
                                <></>
                            );

                        default:
                            return actionGroup.isImplemented ? (
                                <ExpandableRadioButton
                                    disabled={props.disabled}
                                    value={actionGroup.id}
                                    label={actionGroup.name}
                                    isExpanded={
                                        actionTypeValue === actionGroup.id
                                    }
                                    expandableContent={getBuyerActionGroupForm(
                                        actionGroup.id
                                    )}
                                />
                            ) : (
                                <></>
                            );
                    }
                })}
            </RadioGroup>
        );

        const getVetValueActionGroupForm = (
            actionGroup: VetValueActionTypeGroups
        ) => {
            switch (actionGroup) {
                case VetValueActionTypeGroups.BuyerSelectionAndTeaserPosting:
                    return (
                        <Form
                            innerRef={formRef}
                            initialValues={{
                                actionType:
                                    TimelineActionType.VetValueBuyerSelectionAndTeaserPosting,
                            }}
                            onSubmit={async (values) => {
                                props.onSubmit({
                                    ...defaultTimelineAction,
                                    actionType: Number(values.actionType),
                                });
                            }}
                        />
                    );
                default:
                    break;
            }
        };

        const getSellerAndBuyerActionGroupForm = (
            actionGroup: SellerAndBuyerActionTypeGroups
        ) => {
            switch (actionGroup) {
                case SellerAndBuyerActionTypeGroups.BuyerSelection:
                    return (
                        <BuyerSelectionForm
                            ref={formRef}
                            onSubmit={props.onSubmit}
                            disabled={props.disabled}
                            linkedAction={props.linkedAction}
                            currentEvent={props.currentEvent}
                            timelineEvents={props.timelineEvents}
                            party={props.party}
                            documentsSections={props.documentsSections}
                            getDocumentDetails={props.getDocumentDetails}
                            setDocumentSectionList={
                                props.setDocumentSectionList
                            }
                            documentDetailsList={props.documentDetailsList}
                        />
                    );
                case SellerAndBuyerActionTypeGroups.NdaTermsSelection:
                    return (
                        <SellerNdaTermSelectionForm
                            ref={formRef}
                            disabled={props.disabled}
                            party={props.party}
                            onSubmit={props.onSubmit}
                            timelineEvents={props.timelineEvents}
                            currentEvent={props.currentEvent}
                            documentDetailsList={props.documentDetailsList}
                            documentsSections={props.documentsSections}
                            setDocumentSectionList={
                                props.setDocumentSectionList
                            }
                            ndaTermSelectionState={props.ndaTermSelectionState}
                            documentDetails={masterDocumentDetails}
                            linkedAction={props.linkedAction}
                        />
                    );
            }
        };

        const sellerActionsForm = () => (
            <RadioGroup value={actionTypeValue} onChange={handleChange}>
                {SellerLinkActionGroupOptions.map((actionGroup) => {
                    return actionGroup.isImplemented ? (
                        <ExpandableRadioButton
                            disabled={props.disabled}
                            value={actionGroup.id}
                            label={actionGroup.name}
                            isExpanded={actionTypeValue === actionGroup.id}
                            expandableContent={getSellerActionGroupForm(
                                actionGroup.id
                            )}
                        />
                    ) : (
                        <></>
                    );
                })}
            </RadioGroup>
        );

        const vetvalueActionsForm = () => (
            <RadioGroup value={actionTypeValue} onChange={handleChange}>
                {VetValueLinkActionGroupOptions.map((actionGroup) => {
                    return actionGroup.isImplemented ? (
                        <ExpandableRadioButton
                            disabled={props.disabled}
                            value={actionGroup.id}
                            label={actionGroup.name}
                            isExpanded={actionTypeValue === actionGroup.id}
                            expandableContent={getVetValueActionGroupForm(
                                actionGroup.id
                            )}
                        />
                    ) : (
                        <></>
                    );
                })}
            </RadioGroup>
        );

        const sellerAndBuyerActionsForm = () => (
            <RadioGroup value={actionTypeValue} onChange={handleChange}>
                {SellerAndBuyerLinkActionGroupOptions.map((actionGroup) => {
                    return actionGroup.isImplemented ? (
                        <ExpandableRadioButton
                            disabled={props.disabled}
                            value={actionGroup.id}
                            label={actionGroup.name}
                            isExpanded={actionTypeValue === actionGroup.id}
                            expandableContent={getSellerAndBuyerActionGroupForm(
                                actionGroup.id
                            )}
                        />
                    ) : (
                        <></>
                    );
                })}
            </RadioGroup>
        );

        const renderForm = () => {
            switch (props.party) {
                case TimelineParty.Buyer:
                    return buyerActionsForm();
                case TimelineParty.Seller:
                    return sellerActionsForm();
                case TimelineParty.VetValue:
                    return vetvalueActionsForm();
                case TimelineParty.SellerAndBuyer:
                    return sellerAndBuyerActionsForm();
                default:
                    return 'N/A';
            }
        };

        const getOldReferenceIdsList = () => {
            if (
                !documenstHistorySubType ||
                documenstHistorySubType == DocumentDetailsContentSubtype.Blinded
            ) {
                return props.linkedAction?.oldReferenceItemIds;
            } else {
                const subtypeOldRefIds = !props.linkedAction
                    ?.oldReferenceItemAdditionalVersionIds
                    ? []
                    : props.linkedAction?.oldReferenceItemAdditionalVersionIds[
                          subTypeEnumToStringMap[documenstHistorySubType]
                      ];
                return subtypeOldRefIds;
            }
        };

        const filterDocumentDetails = (documentDetails: DocumentDetails[]) => {
            if (!props.linkedAction) return documentDetails;
            let docDetailsList: DocumentDetails[];
            if (
                !documenstHistorySubType ||
                documenstHistorySubType == DocumentDetailsContentSubtype.Blinded
            ) {
                docDetailsList = props.documentDetailsList.filter(
                    (d) =>
                        (d.source === EngagementDocumentSource.AdminUpload ||
                            d.source ===
                                EngagementDocumentSource.BrokerUpload) &&
                        (props.linkedAction.referenceItemId == d.id ||
                            props.linkedAction.oldReferenceItemIds?.includes(
                                d.id
                            ))
                );
            } else {
                const currentMasterOfDoc = documentDetails.find(
                    (d) =>
                        !!d.additionalVersionIds &&
                        d.additionalVersionIds.findIndex(
                            (id) => id == selectedDocumentIdForHistory
                        ) > -1
                );
                const currentAdditionalIds =
                    currentMasterOfDoc?.additionalVersionIds;
                const oldAddRefIds = getOldReferenceIdsList();
                docDetailsList = documentDetails.filter(
                    (d) =>
                        (d.source === EngagementDocumentSource.AdminUpload ||
                            d.source ===
                                EngagementDocumentSource.BrokerUpload) &&
                        d.contentSubtype == documenstHistorySubType &&
                        d.id != d.masterDocumentId &&
                        (currentAdditionalIds?.includes(d.id) ||
                            oldAddRefIds?.includes(d.id))
                );
            }

            docDetailsList = docDetailsList.sort((a, b) => {
                return new Date(a.datePosted) > new Date(b.datePosted) ? -1 : 1;
            });
            return docDetailsList;
        };

        return (
            <>
                {isDocumentsHistoryPopupVisible && (
                    <DocumentsHistoryPopup
                        downloadDocument={props.downloadFile}
                        oldDocumentDetailsList={filterDocumentDetails(
                            props.documentDetailsList
                        )}
                        isVisible={isDocumentsHistoryPopupVisible}
                        onClose={() => {
                            setIsDocumentsHistoryPopupVisible(false);
                            setDocumenstHistorySubType(null);
                            setSelectedDocumentIdForHistory(null);
                        }}
                    />
                )}

                {isTeaserInfoModalVisible && (
                    <TeaserInfoModal
                        timelineId={props.timelineId}
                        teaserData={props.teaserData}
                        isVisible={isTeaserInfoModalVisible}
                        onClose={() => setIsTeaserInfoModalVisible(false)}
                        onSaveToTempState={props.onTeaserSave}
                        onUpdateConfirm={props.onUpdateConfirm}
                        isReadOnly={
                            props.isTeaserConfirmed ||
                            props.currentEvent?.status ===
                                TimelineEventStatus.Completed ||
                            timeline.engagementProcess?.status ===
                                EngagementProcessStatus.Completed
                        }
                    />
                )}

                <div className="action-definition-form">{renderForm()}</div>
            </>
        );
    }
);

export default ActionDefinitionForm;
